import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Check, HelpCircle, Loader2, Webhook } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import type { Binding, ScopedBinding } from "@/app/types";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { isHttpError } from "@/lib/http-error";
import {
  useCreatePlugin,
  useGetPlugins,
  useGetProjectHooksIfReadable,
  useUpdatePlugin,
} from "../api/hooks";
import { HookExecutionPipeline } from "../components/hook-execution-pipeline/hook-execution-pipeline";
import type {
  CreatePluginRequest,
  Hook,
  HookPoint,
  PatchPluginRequest,
  Plugin,
} from "../types";
import { enrichHooksWithPlugins, getDefaultPriorities } from "../utils";

type PluginConfigPageProps = {
  mode: "create" | "edit";
};

const PHASE_ITEMS = { before: "Before", after: "After" };
const FAILURE_MODE_ITEMS = {
  fail_closed: "Block request",
  fail_open: "Allow request",
};
const PLUGIN_TYPE_ITEMS = { webhook: "Webhook" };
const SCOPE_ITEMS = { project: "Project", tenant: "Organization" };
const WEBHOOK_AUTH_TYPE_ITEMS = {
  bearer: "Bearer Token",
  mtls: "mTLS",
  none: "None",
};

// Regex for parsing API validation errors - defined at top level per linter requirements
const FIELD_VALIDATION_REGEX = /for '(\w+)' failed on the '(\w+)'/;

// Token status indicator component to reduce complexity
const TokenStatusIndicator = ({ token }: { token?: { exists?: boolean } }) => {
  if (!token?.exists) {
    return null;
  }
  return (
    <div className="flex items-center gap-2 text-green-600 text-sm">
      <Check className="size-4" />
      <span>Token configured</span>
    </div>
  );
};

// Bearer token input section - extracted to reduce complexity
const BearerTokenInput = ({
  isEditMode,
  token,
  register,
}: {
  isEditMode: boolean;
  token?: { exists?: boolean };
  register: ReturnType<typeof useForm<FormValues>>["register"];
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor="auth-token">
      Bearer Token
      {isEditMode ? <span> (leave empty to keep existing)</span> : null}
    </Label>
    {isEditMode ? <TokenStatusIndicator token={token} /> : null}
    <Input
      id="auth-token"
      placeholder="Enter your bearer token"
      type="password"
      {...register("auth.token")}
    />
    <p className="text-muted-foreground text-xs">
      This token will be sent in the Authorization header of webhook requests.
    </p>
  </div>
);

// Certificate status indicator component
const CertificateStatusIndicator = ({
  configured,
  label,
}: {
  configured?: boolean;
  label: string;
}) => {
  if (!configured) {
    return null;
  }
  return (
    <div className="flex items-center gap-2 text-green-600 text-sm">
      <Check className="size-4" />
      <span>{label} configured</span>
    </div>
  );
};

// mTLS input section - extracted to reduce complexity
const MTLSCertificateInput = ({
  isEditMode,
  auth,
  register,
}: {
  isEditMode: boolean;
  auth?: {
    client_cert_configured?: boolean;
    client_key_configured?: boolean;
    ca_cert?: string;
  };
  register: ReturnType<typeof useForm<FormValues>>["register"];
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-2">
      <Label htmlFor="auth-client-cert">
        Client Certificate
        {isEditMode ? <span> (leave empty to keep existing)</span> : null}
      </Label>
      {isEditMode ? (
        <CertificateStatusIndicator
          configured={auth?.client_cert_configured}
          label="Client certificate"
        />
      ) : null}
      <Textarea
        id="auth-client-cert"
        placeholder="Paste PEM-encoded client certificate"
        rows={4}
        {...register("auth.client_cert")}
      />
      <p className="text-muted-foreground text-xs">
        The client certificate that Arcade will present during TLS handshake
      </p>
    </div>

    <div className="flex flex-col gap-2">
      <Label htmlFor="auth-client-key">
        Client Key
        {isEditMode ? <span> (leave empty to keep existing)</span> : null}
      </Label>
      {isEditMode ? (
        <CertificateStatusIndicator
          configured={auth?.client_key_configured}
          label="Client key"
        />
      ) : null}
      <Textarea
        id="auth-client-key"
        placeholder="Paste PEM-encoded private key"
        rows={4}
        {...register("auth.client_key")}
      />
      <p className="text-muted-foreground text-xs">
        The private key for the client certificate
      </p>
    </div>

    <div className="flex flex-col gap-2">
      <Label htmlFor="auth-ca-cert">
        CA Certificate (optional)
        {isEditMode ? <span> (leave empty to keep existing)</span> : null}
      </Label>
      {isEditMode && auth?.ca_cert ? (
        <CertificateStatusIndicator configured label="CA certificate" />
      ) : null}
      <Textarea
        id="auth-ca-cert"
        placeholder="Paste PEM-encoded CA certificate (optional)"
        rows={4}
        {...register("auth.ca_cert")}
      />
      <p className="text-muted-foreground text-xs">
        Custom CA certificate for server verification
      </p>
    </div>
  </div>
);

// Form schema
const endpointSchema = z
  .object({
    enabled: z.boolean(),
    url: z.string().optional(),
    status: z.enum(["active", "inactive"]),
    phase: z.enum(["before", "after"]),
    failureMode: z.enum(["fail_closed", "fail_open"]),
    priority: z
      .number()
      .min(0, "Priority must be at least 0")
      .max(1000, "Priority must be at most 1000"),
    timeoutSeconds: z
      .number()
      .min(0.1, "Timeout must be at least 0.1 seconds")
      .max(120, "Timeout must be at most 120 seconds")
      .optional(),
    cacheEnabled: z.boolean().optional(),
    cacheTtlSeconds: z.number().min(1).max(604_800).optional(), // Max 1 week
  })
  .refine(
    (data) => {
      // If not enabled, URL is not required
      if (!data.enabled) {
        return true;
      }
      // If enabled, URL is required and must be valid
      if (!data.url) {
        return false;
      }
      try {
        new URL(data.url);
        return true;
      } catch {
        return false;
      }
    },
    { message: "Valid URL is required when endpoint is enabled", path: ["url"] }
  );

const formSchema = z
  .object({
    pluginType: z.enum(["webhook"]),
    name: z.string().min(1, "Name is required").max(256, "Name is too long"),
    description: z.string().max(1000, "Description is too long").optional(),
    scope: z.enum(["tenant", "project"]),
    status: z.enum(["active", "inactive"]),
    endpoints: z.object({
      access: endpointSchema.optional(),
      pre: endpointSchema.optional(),
      post: endpointSchema.optional(),
    }),
    auth: z.object({
      type: z.enum(["bearer", "mtls", "none"]),
      token: z.string().optional(),
      client_cert: z.string().optional(),
      client_key: z.string().optional(),
      ca_cert: z.string().optional(),
    }),
  })
  .refine(
    (data) => {
      // If auth type is mtls, client_cert and client_key are required (for create mode)
      // In edit mode, these can be empty since existing values are kept
      if (data.auth.type === "mtls") {
        const hasClientCert =
          !!data.auth.client_cert && data.auth.client_cert.trim().length > 0;
        const hasClientKey =
          !!data.auth.client_key && data.auth.client_key.trim().length > 0;
        // Both must be provided together, or neither (in edit mode, existing values are kept)
        return (
          (hasClientCert && hasClientKey) || !(hasClientCert || hasClientKey)
        );
      }
      return true;
    },
    {
      message:
        "Both client certificate and key are required for mTLS authentication",
      path: ["auth", "client_cert"],
    }
  );

type FormValues = z.infer<typeof formSchema>;

// Helper to get endpoint form values from plugin and hook data
const getEndpointDefaults = (
  endpoint: { url: string; timeout_ms: number } | undefined,
  hook: Hook | undefined,
  defaultPhase: "before" | "after",
  defaultPriority: number
): FormValues["endpoints"]["access"] => {
  const defaultTimeoutSeconds = 5; // 5 seconds default
  if (endpoint?.url) {
    return {
      enabled: true,
      url: endpoint.url,
      status: hook?.status ?? "active",
      phase: hook?.phase ?? defaultPhase,
      failureMode: hook?.failure_mode ?? "fail_closed",
      priority: hook?.priority ?? defaultPriority,
      timeoutSeconds: endpoint.timeout_ms
        ? endpoint.timeout_ms / 1000
        : defaultTimeoutSeconds,
      cacheEnabled: hook?.cache_enabled ?? false,
      // Convert milliseconds from API to seconds for form
      cacheTtlSeconds: hook?.cache_ttl_ms
        ? hook.cache_ttl_ms / 1000
        : undefined,
    };
  }
  return {
    enabled: false,
    url: "",
    status: "active",
    phase: defaultPhase,
    failureMode: "fail_closed",
    priority: defaultPriority,
    timeoutSeconds: defaultTimeoutSeconds,
    cacheEnabled: false,
    cacheTtlSeconds: undefined,
  };
};

const getDefaultValues = (
  priorities: Record<string, number>,
  plugin?: Plugin,
  hooks?: Hook[]
): FormValues => {
  if (plugin) {
    // Find hooks for this plugin
    const pluginHooks = hooks?.filter((h) => h.plugin_id === plugin.id) ?? [];
    const accessHook = pluginHooks.find((h) => h.hook_point === "tool.access");
    const preHook = pluginHooks.find((h) => h.hook_point === "tool.pre");
    const postHook = pluginHooks.find((h) => h.hook_point === "tool.post");

    return {
      pluginType: "webhook",
      name: plugin.name,
      description: plugin.description ?? "",
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      scope: plugin.binding.type as "tenant" | "project",
      status: plugin.status,
      endpoints: {
        access: getEndpointDefaults(
          plugin.webhook_config?.endpoints.access,
          accessHook,
          "before",
          priorities["tool.access"] ?? 0
        ),
        pre: getEndpointDefaults(
          plugin.webhook_config?.endpoints.pre,
          preHook,
          "before",
          priorities["tool.pre"] ?? 0
        ),
        post: getEndpointDefaults(
          plugin.webhook_config?.endpoints.post,
          postHook,
          "after",
          priorities["tool.post"] ?? 0
        ),
      },
      auth: {
        type:
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          (plugin.webhook_config?.auth?.type as "bearer" | "mtls") ?? "bearer",
        token: "",
        client_cert: "",
        client_key: "",
        ca_cert: "",
      },
    };
  }

  return {
    pluginType: "webhook",
    name: "",
    description: "",
    scope: "project",
    status: "active",
    endpoints: {
      access: {
        enabled: false,
        url: "",
        status: "active",
        phase: "before",
        failureMode: "fail_closed",
        priority: priorities["tool.access"] ?? 0,
        timeoutSeconds: 5,
        cacheEnabled: false,
        cacheTtlSeconds: undefined,
      },
      pre: {
        enabled: false,
        url: "",
        status: "active",
        phase: "before",
        failureMode: "fail_closed",
        priority: priorities["tool.pre"] ?? 0,
        timeoutSeconds: 5,
        cacheEnabled: false,
        cacheTtlSeconds: undefined,
      },
      post: {
        enabled: false,
        url: "",
        status: "active",
        phase: "after",
        failureMode: "fail_closed",
        priority: priorities["tool.post"] ?? 0,
        timeoutSeconds: 5,
        cacheEnabled: false,
        cacheTtlSeconds: undefined,
      },
    },
    auth: {
      type: "bearer",
      token: "",
      client_cert: "",
      client_key: "",
      ca_cert: "",
    },
  };
};

// Helper to build endpoint config from form values
const buildEndpointConfig = (endpoint: FormValues["endpoints"]["access"]) => {
  if (!(endpoint?.enabled && endpoint.url)) {
    return;
  }
  return {
    url: endpoint.url,
    status: endpoint.status ?? "active",
    phase: endpoint.phase,
    failure_mode: endpoint.failureMode,
    priority: endpoint.priority,
    timeout_ms: endpoint.timeoutSeconds
      ? Math.round(endpoint.timeoutSeconds * 1000)
      : undefined,
    cache_enabled: endpoint.cacheEnabled,
    // Convert seconds to milliseconds for API
    cache_ttl_ms: endpoint.cacheTtlSeconds
      ? endpoint.cacheTtlSeconds * 1000
      : undefined,
  };
};

// Helper to build the auth config from form values
const buildAuthConfig = (auth: FormValues["auth"]) => {
  if (auth.type === "none") {
    return { type: "none" as const };
  }
  if (auth.type === "bearer" && auth.token) {
    return { type: "bearer" as const, token: auth.token };
  }
  if (auth.type === "mtls" && auth.client_cert && auth.client_key) {
    return {
      type: "mtls" as const,
      client_cert: auth.client_cert,
      client_key: auth.client_key,
      ca_cert: auth.ca_cert || undefined,
    };
  }
  return;
};

// Helper to build the full request payload for creating
const buildCreatePayload = (values: FormValues): CreatePluginRequest => ({
  plugin_type: values.pluginType,
  name: values.name,
  description: values.description || undefined,
  status: values.status,
  binding_type: values.scope,
  webhook_config: {
    endpoints: {
      access: buildEndpointConfig(values.endpoints.access),
      pre: buildEndpointConfig(values.endpoints.pre),
      post: buildEndpointConfig(values.endpoints.post),
    },
    auth: buildAuthConfig(values.auth),
  },
});

// Helper to build the patch payload for updating
const buildPatchPayload = (values: FormValues): PatchPluginRequest => {
  const payload: PatchPluginRequest = {
    name: values.name,
    description: values.description || undefined,
    status: values.status,
    webhook_config: {
      endpoints: {},
    },
  };

  // Build endpoint configs
  const accessConfig = buildEndpointConfig(values.endpoints.access);
  const preConfig = buildEndpointConfig(values.endpoints.pre);
  const postConfig = buildEndpointConfig(values.endpoints.post);

  if (accessConfig && payload.webhook_config?.endpoints) {
    payload.webhook_config.endpoints.access = accessConfig;
  }
  if (preConfig && payload.webhook_config?.endpoints) {
    payload.webhook_config.endpoints.pre = preConfig;
  }
  if (postConfig && payload.webhook_config?.endpoints) {
    payload.webhook_config.endpoints.post = postConfig;
  }

  // Include auth if credentials are provided
  const authConfig = buildAuthConfig(values.auth);
  if (authConfig && payload.webhook_config) {
    payload.webhook_config.auth = authConfig;
  }

  return payload;
};

// Validation type to user-friendly message templates
const VALIDATION_MESSAGES: Record<string, (field: string) => string> = {
  min: (field) => `${field} value is too low. Please use a valid value.`,
  max: (field) => `${field} value is too high. Please use a valid value.`,
  required: (field) => `${field} is required.`,
  url: (field) => `${field} must be a valid URL.`,
};

// HTTP status code to user-friendly messages
const STATUS_MESSAGES: Record<number, string> = {
  400: "Invalid request. Please check your input values.",
  401: "You are not authorized to perform this action.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "A system with this name already exists.",
  500: "An unexpected server error occurred. Please try again.",
};

// Parse field validation errors from API response
const parseFieldValidationError = (message: string): string | null => {
  const fieldMatch = message.match(FIELD_VALIDATION_REGEX);
  if (!fieldMatch) {
    return null;
  }

  const [, fieldName, validationType] = fieldMatch;
  const messageTemplate = VALIDATION_MESSAGES[validationType];
  return messageTemplate
    ? messageTemplate(fieldName)
    : `Invalid ${fieldName.toLowerCase()}. Please check your input.`;
};

// Helper to parse API errors into user-friendly messages
const parseApiError = (error: unknown): string => {
  if (error instanceof Error && !isHttpError(error)) {
    return error.message;
  }

  if (!isHttpError(error)) {
    return "An unexpected error occurred.";
  }

  const data = error.data;

  // Handle structured validation errors from the API
  if (typeof data === "object" && data !== null) {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const record = data as Record<string, unknown>;
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const message = (record.message || record.error || "") as string;

    if (message.includes("Field validation")) {
      const parsed = parseFieldValidationError(message);
      if (parsed) {
        return parsed;
      }
    }

    if (message) {
      return message;
    }
  }

  // Fallback to status-based messages
  const status = error.status;
  return status
    ? (STATUS_MESSAGES[status] ?? "An error occurred. Please try again.")
    : "An error occurred. Please try again.";
};

// Endpoint configuration component
type EndpointConfigProps = {
  endpointKey: "access" | "pre" | "post";
  label: string;
  description: string;
  isProjectScope: boolean;
};

const EndpointConfig = ({
  endpointKey,
  label,
  description,
  isProjectScope,
}: EndpointConfigProps) => {
  const { register, watch, setValue, formState } = useFormContext<FormValues>();
  const fieldPath = `endpoints.${endpointKey}` as const;
  const endpoint = watch(fieldPath);
  const enabled = endpoint?.enabled ?? false;

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const errors = formState.errors.endpoints as
    | Record<
        string,
        { url?: { message?: string }; priority?: { message?: string } }
      >
    | undefined;
  const urlError = errors?.[endpointKey]?.url?.message;
  const priorityError = errors?.[endpointKey]?.priority?.message;

  return (
    <Card className={enabled ? "" : "opacity-60"}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              checked={enabled}
              className="size-4 rounded border-input"
              id={`${endpointKey}-enabled`}
              onChange={(e) =>
                setValue(`${fieldPath}.enabled`, e.target.checked, {
                  shouldDirty: true,
                })
              }
              type="checkbox"
            />
            <div>
              <CardTitle className="text-base">{label}</CardTitle>
              <CardDescription className="text-xs">
                {description}
              </CardDescription>
            </div>
          </div>
          {!!enabled && (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "font-medium text-xs",
                  endpoint?.status === "active"
                    ? "text-green-600"
                    : "text-muted-foreground"
                )}
              >
                {endpoint?.status === "active" ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={endpoint?.status === "active"}
                onCheckedChange={(checked) =>
                  setValue(
                    `${fieldPath}.status`,
                    checked ? "active" : "inactive",
                    { shouldDirty: true }
                  )
                }
              />
            </div>
          )}
        </div>
      </CardHeader>
      {!!enabled && (
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${endpointKey}-url`}>Webhook URL</Label>
            <Input
              id={`${endpointKey}-url`}
              placeholder="https://your-server.com/webhook"
              {...register(`${fieldPath}.url`)}
            />
            {!!urlError && (
              <p className="text-destructive text-xs">{urlError}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${endpointKey}-priority`}>
                Priority (0-1000)
              </Label>
              <Input
                className={priorityError ? "border-destructive" : ""}
                id={`${endpointKey}-priority`}
                max={1000}
                min={0}
                type="number"
                {...register(`${fieldPath}.priority`, { valueAsNumber: true })}
              />
              {priorityError ? (
                <p className="text-destructive text-xs">{priorityError}</p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Lower numbers run first
                </p>
              )}
            </div>
            {!isProjectScope && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor={`${endpointKey}-phase`}>Phase</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="size-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>
                        Controls when this hook runs relative to project hooks.
                        "Before" runs first (e.g., for compliance checks),
                        "After" runs last (e.g., for auditing).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Select
                  items={PHASE_ITEMS}
                  onValueChange={(value) =>
                    setValue(
                      `${fieldPath}.phase`,
                      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                      value as "before" | "after",
                      {
                        shouldDirty: true,
                      }
                    )
                  }
                  value={endpoint?.phase ?? "before"}
                >
                  <SelectTrigger id={`${endpointKey}-phase`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <Label htmlFor={`${endpointKey}-failure`}>On Failure</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>
                      Block request: stops execution if this hook fails
                      (recommended for security). Allow request: continues
                      execution even if the hook fails.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                items={FAILURE_MODE_ITEMS}
                onValueChange={(value) =>
                  setValue(
                    `${fieldPath}.failureMode`,
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    value as "fail_closed" | "fail_open",
                    { shouldDirty: true }
                  )
                }
                value={endpoint?.failureMode ?? "fail_closed"}
              >
                <SelectTrigger id={`${endpointKey}-failure`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fail_closed">Block request</SelectItem>
                  <SelectItem value="fail_open">Allow request</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Timeout */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={`${endpointKey}-timeout`}>Timeout</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="size-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Maximum time to wait for the webhook to respond before
                      timing out. Range: 0.1 - 120 seconds.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  className="w-24"
                  id={`${endpointKey}-timeout`}
                  max="120"
                  min="0.1"
                  step="0.1"
                  type="number"
                  {...register(`${fieldPath}.timeoutSeconds`, {
                    valueAsNumber: true,
                  })}
                />
                <span className="text-muted-foreground text-sm">seconds</span>
              </div>
            </div>
          </div>

          {/* Cache Configuration */}
          <div className="flex flex-col gap-3 border-t pt-4">
            <div className="flex items-center gap-2">
              <input
                checked={endpoint?.cacheEnabled ?? false}
                className="size-4 rounded border-gray-300"
                id={`${endpointKey}-cache-enabled`}
                onChange={(e) =>
                  setValue(`${fieldPath}.cacheEnabled`, e.target.checked, {
                    shouldDirty: true,
                  })
                }
                type="checkbox"
              />
              <div className="flex items-center gap-1">
                <Label
                  className="cursor-pointer"
                  htmlFor={`${endpointKey}-cache-enabled`}
                >
                  Enable response caching
                </Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="size-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>
                      Cache successful hook responses to improve performance.
                      Cached responses are keyed by request parameters and
                      automatically invalidated when configurations change.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            {endpoint?.cacheEnabled ? (
              <div className="ml-6 flex flex-col gap-2">
                <Label htmlFor={`${endpointKey}-cache-ttl`}>
                  Cache TTL (seconds)
                </Label>
                <Input
                  id={`${endpointKey}-cache-ttl`}
                  max={604_800}
                  min={1}
                  placeholder="300"
                  type="number"
                  {...register(`${fieldPath}.cacheTtlSeconds`, {
                    valueAsNumber: true,
                    setValueAs: (v) => (v === "" ? undefined : Number(v)),
                  })}
                />
                <p className="text-muted-foreground text-sm">
                  Time in seconds before cached responses expire (1-604800, max
                  1 week)
                </p>
              </div>
            ) : null}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const PluginConfigPage = ({ mode }: PluginConfigPageProps) => {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/contextual-access",
  });
  const params = useParams({ strict: false });
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const pluginId = (params as { pluginId?: string }).pluginId;
  const isEditMode = mode === "edit";

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch existing data
  const { data: allPlugins = [] } = useGetPlugins();
  const allHooks = useGetProjectHooksIfReadable();

  // Find the plugin being edited (if in edit mode)
  const plugin = useMemo(
    () =>
      mode === "edit" ? allPlugins.find((p) => p.id === pluginId) : undefined,
    [mode, allPlugins, pluginId]
  );

  const binding: ScopedBinding =
    plugin?.binding.type === "tenant" ? "tenant" : "project";

  // Enrich hooks for preview
  const enrichedHooks = useMemo(
    () => enrichHooksWithPlugins(allHooks, allPlugins),
    [allHooks, allPlugins]
  );

  // Filter out this plugin's hooks for preview (in edit mode)
  const otherHooks = useMemo(
    () =>
      mode === "edit" && plugin
        ? enrichedHooks.filter((h) => h.plugin_id !== plugin.id)
        : enrichedHooks,
    [mode, enrichedHooks, plugin]
  );

  // For create mode, start with project scope. For edit mode, use the plugin's binding.
  const initialScope: Binding = isEditMode ? binding : "project";

  // Calculate default priorities based on the selected scope
  // This filters hooks to only consider those with matching binding type
  const [selectedScope, setSelectedScope] = useState<Binding>(initialScope);

  const defaultPriorities = useMemo(
    () => getDefaultPriorities(enrichedHooks, selectedScope),
    [enrichedHooks, selectedScope]
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(defaultPriorities, plugin, allHooks),
  });

  // Reset form when plugin data loads (for edit mode)
  useEffect(() => {
    if (mode === "edit" && plugin) {
      form.reset(getDefaultValues(defaultPriorities, plugin, allHooks));
    }
  }, [mode, plugin, defaultPriorities, form, allHooks]);

  // Use useWatch for reactive updates when form values change
  const endpoints = useWatch({ control: form.control, name: "endpoints" });
  const pluginName = useWatch({ control: form.control, name: "name" });
  const scope = useWatch({ control: form.control, name: "scope" });

  // Update priorities when scope changes in create mode
  // Update ALL endpoint priorities regardless of enabled state so they're ready when enabled
  useEffect(() => {
    if (mode === "create" && scope !== selectedScope) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      setSelectedScope(scope as ScopedBinding);
      const newPriorities = getDefaultPriorities(
        enrichedHooks,
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        scope as ScopedBinding
      );
      // Update all endpoint priorities, not just enabled ones
      form.setValue("endpoints.access.priority", newPriorities["tool.access"]);
      form.setValue("endpoints.pre.priority", newPriorities["tool.pre"]);
      form.setValue("endpoints.post.priority", newPriorities["tool.post"]);
    }
  }, [scope, selectedScope, mode, enrichedHooks, form]);

  // Build pending hooks for preview
  // Use scope for create mode, binding for edit mode
  const effectiveBinding = mode === "create" ? scope : binding;
  const isProjectScope = effectiveBinding === "project";

  const pendingHooks = useMemo(() => {
    const pending: Array<{
      hookPoint: HookPoint;
      priority: number;
      phase: "before" | "after";
      binding: "tenant" | "project";
      name?: string;
    }> = [];

    const hookBinding = effectiveBinding === "tenant" ? "tenant" : "project";

    if (endpoints.access?.enabled) {
      pending.push({
        hookPoint: "tool.access",
        priority: endpoints.access.priority,
        phase: endpoints.access.phase,
        binding: hookBinding,
        name: pluginName || "New System",
      });
    }
    if (endpoints.pre?.enabled) {
      pending.push({
        hookPoint: "tool.pre",
        priority: endpoints.pre.priority,
        phase: endpoints.pre.phase,
        binding: hookBinding,
        name: pluginName || "New System",
      });
    }
    if (endpoints.post?.enabled) {
      pending.push({
        hookPoint: "tool.post",
        priority: endpoints.post.priority,
        phase: endpoints.post.phase,
        binding: hookBinding,
        name: pluginName || "New System",
      });
    }

    return pending;
  }, [endpoints, pluginName, effectiveBinding]);

  const createPluginProject = useCreatePlugin("project");
  const createPluginTenant = useCreatePlugin("tenant");
  const updatePluginMutation = useUpdatePlugin(pluginId ?? "", binding);

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const payload = buildCreatePayload(values);
        const mutation =
          values.scope === "tenant" ? createPluginTenant : createPluginProject;
        await mutation.mutateAsync(payload);
      } else if (plugin) {
        const payload = buildPatchPayload(values);
        await updatePluginMutation.mutateAsync(payload);
      }

      navigate({
        to: ".",
      });
    } catch (error) {
      // Parse and display user-friendly error messages
      const errorMessage = parseApiError(error);
      toast.error("Failed to save system", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate({
      to: ".",
    });
  };

  const getButtonLabel = () => {
    if (isSubmitting) {
      return mode === "create" ? "Creating..." : "Saving...";
    }
    return mode === "create" ? "Add Extension" : "Save Changes";
  };

  // Show loading state if editing but plugin not found yet
  if (isEditMode && !plugin && pluginId) {
    return (
      <ListLayout description="Loading..." title="Edit System">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </ListLayout>
    );
  }

  return (
    <ListLayout
      actions={
        <div className="flex items-center gap-2">
          <Button onClick={handleCancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
            type="button"
          >
            {!!isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            {getButtonLabel()}
          </Button>
        </div>
      }
      description={
        mode === "create"
          ? "Configure an extension to control tool access, validate requests, and filter outputs."
          : `Configure settings for ${plugin?.name ?? "this extension"}`
      }
      title={
        mode === "create"
          ? "Add Extension"
          : `Edit ${plugin?.name ?? "Extension"}`
      }
    >
      <Button
        className="mb-4 w-fit"
        onClick={handleCancel}
        size="sm"
        variant="ghost"
      >
        <ArrowLeft className="mr-2 size-4" />
        Back to Contextual Access
      </Button>

      <FormProvider {...form}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left column - Configuration */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {/* System Type */}
                <div className="flex flex-col gap-2">
                  <Label>System Type</Label>
                  <Select
                    disabled={isEditMode}
                    items={PLUGIN_TYPE_ITEMS}
                    onValueChange={(value) =>
                      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                      form.setValue("pluginType", value as "webhook")
                    }
                    value={form.watch("pluginType")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="webhook">
                        <div className="flex items-center gap-2">
                          <Webhook className="size-4" />
                          <span>Webhook</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    HTTP endpoints that receive requests for each hook point
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="My Extension"
                    {...form.register("name")}
                  />
                  {!!form.formState.errors.name && (
                    <p className="text-destructive text-xs">
                      {form.formState.errors.name.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="What does this extension do?"
                    rows={2}
                    {...form.register("description")}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>Scope</Label>
                    <Select
                      disabled={isEditMode}
                      items={SCOPE_ITEMS}
                      onValueChange={(value) =>
                        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                        form.setValue("scope", value as "tenant" | "project")
                      }
                      value={scope}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project">Project</SelectItem>
                        <SelectItem value="tenant">Organization</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      {scope === "tenant"
                        ? "Applies to all projects in the organization"
                        : "Applies to this project only"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Webhook Endpoints */}
            <Card>
              <CardHeader>
                <CardTitle>Webhook Endpoints</CardTitle>
                <CardDescription>
                  Configure which stages of tool execution to intercept. Each
                  endpoint receives a request and can allow, deny, or modify the
                  operation.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <EndpointConfig
                  description="Control which users can see and use each tool"
                  endpointKey="access"
                  isProjectScope={isProjectScope}
                  label="Tool Access"
                />
                <EndpointConfig
                  description="Validate and enrich tool inputs before execution"
                  endpointKey="pre"
                  isProjectScope={isProjectScope}
                  label="Pre tool execution"
                />
                <EndpointConfig
                  description="Filter or redact sensitive data from tool outputs"
                  endpointKey="post"
                  isProjectScope={isProjectScope}
                  label="Post tool execution"
                />
              </CardContent>
            </Card>

            {/* Authentication */}
            <Card>
              <CardHeader>
                <CardTitle>Authentication</CardTitle>
                <CardDescription>
                  Configure how requests to your webhooks are authenticated.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label>Authentication Type</Label>
                  <Select
                    items={WEBHOOK_AUTH_TYPE_ITEMS}
                    onValueChange={(value) =>
                      form.setValue(
                        "auth.type",
                        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                        value as "bearer" | "mtls" | "none"
                      )
                    }
                    value={form.watch("auth.type")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="mtls">mTLS</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.watch("auth.type") === "bearer" ? (
                  <BearerTokenInput
                    isEditMode={isEditMode}
                    register={form.register}
                    token={plugin?.webhook_config?.auth?.token}
                  />
                ) : null}

                {form.watch("auth.type") === "mtls" ? (
                  <MTLSCertificateInput
                    auth={plugin?.webhook_config?.auth}
                    isEditMode={isEditMode}
                    register={form.register}
                  />
                ) : null}
              </CardContent>
            </Card>
          </div>

          {/* Right column - Execution Order Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <Card>
                <CardHeader>
                  <CardTitle>Execution Order Preview</CardTitle>
                  <CardDescription>
                    See where your hooks will execute in the pipeline. Adjust
                    priority to change the order.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <HookExecutionPipeline
                    compact
                    hooks={otherHooks}
                    pendingHooks={pendingHooks}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </FormProvider>
    </ListLayout>
  );
};
